import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  CircleSlash,
  History,
  Loader2,
  Sparkles,
  Wand2,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import {
  useProject,
  useProjectGlossary,
  useProjectKeys,
  useProjectPermissions,
  useProjectTranslations,
  useRunTranslate,
  useSelf,
  useTranslationRevisions,
  useUpsertTranslation,
  useWorkspaceMembers,
  type Key,
  type TranslateProgress,
  type Translation,
  type TranslationStatus,
} from '@/lib/api';
import { canWriteLang } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { KeyListItem } from '@/components/editor/KeyListItem';
import { SuggestionRibbon } from '@/components/editor/SuggestionRibbon';
import { HistoryDrawer } from '@/components/editor/HistoryDrawer';
import {
  ALL_STATUSES,
  emptyFilters,
  filterKeys,
  nextKeyId,
  rowStatus,
  statusCounts,
  STATUS_LABEL,
  STATUS_TONE,
  translationByKey,
  uniqueNamespaces,
  type EditorFilters,
  type StatusFilter,
} from '@/components/editor/translatorEditorState';

export function TranslateTab() {
  const { wsId = '', projId = '' } = useParams<{ wsId: string; projId: string }>();
  const toast = useToast();

  const project = useProject(projId);
  const self = useSelf();
  const members = useWorkspaceMembers(wsId);
  const perms = useProjectPermissions(projId);
  const glossary = useProjectGlossary(projId);
  const keys = useProjectKeys(projId);

  const targets = useMemo(
    () => project.data?.targetLanguageCodes ?? [],
    [project.data?.targetLanguageCodes],
  );
  const [lang, setLang] = useState<string>('');
  useEffect(() => {
    if (!lang && targets.length > 0 && targets[0]) setLang(targets[0]);
  }, [lang, targets]);

  const translations = useProjectTranslations(projId, lang || undefined);
  const upsert = useUpsertTranslation();
  const runTranslate = useRunTranslate();

  const [filters, setFilters] = useState<EditorFilters>(emptyFilters);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<string>('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [batchProgress, setBatchProgress] = useState<TranslateProgress | null>(null);
  const [lastAISuggestion, setLastAISuggestion] = useState<{
    keyId: string;
    value: string;
    provider: string;
    model: string;
    latencyMs?: number;
  } | null>(null);

  const filteredKeys = useMemo(
    () => filterKeys(keys.data ?? [], translations.data, filters),
    [keys.data, translations.data, filters],
  );
  const namespaces = useMemo(() => uniqueNamespaces(keys.data ?? []), [keys.data]);
  const counts = useMemo(
    () => statusCounts(keys.data ?? [], translations.data),
    [keys.data, translations.data],
  );
  const byKey = useMemo(() => translationByKey(translations.data), [translations.data]);

  useEffect(() => {
    if (!selectedKeyId && filteredKeys[0]) setSelectedKeyId(filteredKeys[0].id);
    if (selectedKeyId && !filteredKeys.find((k) => k.id === selectedKeyId)) {
      setSelectedKeyId(filteredKeys[0]?.id ?? null);
    }
  }, [filteredKeys, selectedKeyId]);

  const selectedKey: Key | undefined = useMemo(
    () => filteredKeys.find((k) => k.id === selectedKeyId) ?? undefined,
    [filteredKeys, selectedKeyId],
  );
  const selectedTr: Translation | undefined = selectedKey ? byKey.get(selectedKey.id) : undefined;

  // Sync draft buffer with whichever (key, lang) pair is selected.
  useEffect(() => {
    setDraftValue(selectedTr?.value ?? '');
  }, [selectedKey?.id, lang, selectedTr?.value]);

  const dirty = (selectedTr?.value ?? '') !== draftValue;
  const writable = canWriteLang(self.data, members.data, perms.data, projId, lang);
  const writableAnyLang = targets.some((l) =>
    canWriteLang(self.data, members.data, perms.data, projId, l),
  );

  const select = useCallback((id: string | null) => {
    if (!id) return;
    setSelectedKeyId(id);
  }, []);

  async function saveCurrent(opts?: {
    status?: TranslationStatus;
    advance?: boolean;
    valueOverride?: string;
  }) {
    if (!selectedKey || !lang) return;
    if (!writable) {
      toast.show('You do not have write access for this language', 'error');
      return;
    }
    const value = opts?.valueOverride ?? draftValue;
    try {
      await upsert.mutateAsync({
        keyID: selectedKey.id,
        lang,
        projectID: projId,
        value,
        status: opts?.status ?? 'reviewed',
        source: 'editor',
      });
      toast.show(opts?.status === 'approved' ? 'Approved' : 'Saved', 'success');
      if (opts?.advance) {
        const nextId = nextKeyId(filteredKeys, selectedKey.id, 1);
        select(nextId);
      }
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onReject() {
    if (!selectedKey || !lang) return;
    if (!writable) return;
    try {
      await upsert.mutateAsync({
        keyID: selectedKey.id,
        lang,
        projectID: projId,
        value: '',
        status: 'empty',
        source: 'editor:reject',
      });
      setDraftValue('');
      toast.show('Cleared', 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onTranslateAgain() {
    if (!selectedKey || !lang) return;
    try {
      const res = await runTranslate.mutateAsync({
        projectID: projId,
        languages: [lang],
        // dryRun so we don't overwrite without the user's review.
        dryRun: true,
        batchSize: 1,
      });
      const hint = res.fuzzy?.find((f) => f.keyId === selectedKey.id && f.language === lang);
      setLastAISuggestion({
        keyId: selectedKey.id,
        value: hint?.value ?? '',
        provider: res.provider,
        model: res.model,
        latencyMs: res.durationMs,
      });
      if (!hint?.value) {
        // dryRun returns stats only — fall back to fetching the persisted result
        // by issuing a real translate for the single missing entry. The backend
        // skips keys that already have a non-empty value when onlyMissing.
        toast.show(
          `Sent for translation (${res.provider}). Run "Translate missing" to persist.`,
          'info',
        );
      }
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onBatchTranslate(scope: 'empty' | 'missing') {
    // The run is an async background job now: kick it off, then track the job's
    // live counters so a large project shows progress instead of awaiting one
    // blocking request (which the 30s timeout used to cap at ~280 entries).
    setBatchProgress({ status: 'queued', total: 0, translated: 0, failed: 0, remaining: 0 });
    try {
      const res = await runTranslate.mutateAsync({
        projectID: projId,
        languages: lang ? [lang] : undefined,
        onlyMissing: true,
        includeFuzzy: scope === 'missing',
        onProgress: setBatchProgress,
      });
      const translated = res.languages.reduce((n, l) => n + (l.translated ?? 0), 0);
      const errors = res.languages.reduce((n, l) => n + (l.errors ?? 0), 0);
      const fmt = scope === 'empty' ? 'empty' : 'missing/fuzzy';
      const meta = res.provider ? ` (${res.provider} ${res.model})` : '';
      if (errors > 0) {
        toast.show(
          `Translated ${translated} ${fmt} ${translated === 1 ? 'entry' : 'entries'}, ${errors} failed${meta}`,
          'warning',
        );
      } else {
        toast.show(
          `Translated ${translated} ${fmt} ${translated === 1 ? 'entry' : 'entries'}${meta}`,
          'success',
        );
      }
    } catch (err) {
      toast.show((err as Error).message, 'error');
    } finally {
      setBatchProgress(null);
    }
  }

  // Keyboard-only flow: j/k navigate, Cmd-Enter save+next, Esc cancels edits.
  // The key list itself is a scrollable container that owns the keydown handler
  // so the textarea doesn't swallow j/k while editing.
  const listRef = useRef<HTMLDivElement | null>(null);
  function onListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      select(nextKeyId(filteredKeys, selectedKeyId, 1));
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      select(nextKeyId(filteredKeys, selectedKeyId, -1));
    }
  }

  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  function onTextareaKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      setDraftValue(selectedTr?.value ?? '');
      editorRef.current?.blur();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void saveCurrent({ advance: true });
    } else if (e.key === 'j' && e.altKey) {
      e.preventDefault();
      select(nextKeyId(filteredKeys, selectedKeyId, 1));
    } else if (e.key === 'k' && e.altKey) {
      e.preventDefault();
      select(nextKeyId(filteredKeys, selectedKeyId, -1));
    }
  }

  const langSelectId = useId();
  const filterQueryId = useId();

  // Loading / empty rails
  if (project.isLoading) return <Spinner label="Loading project…" />;
  if (!project.data) return null;
  if (targets.length === 0) {
    return (
      <EmptyState
        icon={CircleSlash}
        title="No target languages configured"
        description="Add target languages in Project settings before translators can start working."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor={langSelectId}>Target language</Label>
            <select
              id={langSelectId}
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="flex h-9 min-w-[10rem] rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {targets.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={filterQueryId}>Search</Label>
            <Input
              id={filterQueryId}
              value={filters.query}
              placeholder="Key, source, or translation…"
              onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
              className="w-72"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void onBatchTranslate('empty')}
            disabled={!writable || runTranslate.isPending}
          >
            {runTranslate.isPending ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <Wand2 aria-hidden />
            )}
            Translate empty
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void onBatchTranslate('missing')}
            disabled={!writable || runTranslate.isPending}
          >
            <Sparkles aria-hidden />
            Translate missing
          </Button>
        </div>
      </div>

      {batchProgress && <BatchProgressBar progress={batchProgress} />}

      <FilterChips
        counts={counts}
        active={filters.statuses}
        onToggle={(s) =>
          setFilters((f) => {
            const next = new Set(f.statuses);
            if (next.has(s)) next.delete(s);
            else next.add(s);
            return { ...f, statuses: next };
          })
        }
      />

      {namespaces.length > 1 && (
        <NamespaceChips
          namespaces={namespaces}
          active={filters.namespaces}
          onToggle={(ns) =>
            setFilters((f) => {
              const next = new Set(f.namespaces);
              if (next.has(ns)) next.delete(ns);
              else next.add(ns);
              return { ...f, namespaces: next };
            })
          }
        />
      )}

      {!writableAnyLang && (
        <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          You have read-only access to this project. Browse freely — saves and approvals are
          disabled.
        </p>
      )}

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[20rem_1fr]">
        <div
          ref={listRef}
          tabIndex={0}
          onKeyDown={onListKeyDown}
          aria-label="Translation keys"
          className="max-h-[70vh] overflow-y-auto rounded-md border border-border bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {keys.isLoading ? (
            <div className="p-4">
              <Spinner label="Loading keys…" />
            </div>
          ) : filteredKeys.length === 0 ? (
            <p className="px-4 py-6 text-xs text-muted-foreground">
              {(keys.data ?? []).length === 0
                ? 'No keys yet — import a source file or push from the CLI.'
                : 'No keys match the current filters.'}
            </p>
          ) : (
            <ul role="listbox" aria-label="Keys">
              {filteredKeys.map((k) => (
                <KeyListItem
                  key={k.id}
                  k={k}
                  translation={byKey.get(k.id)}
                  selected={k.id === selectedKeyId}
                  dirty={k.id === selectedKeyId && dirty}
                  onSelect={select}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-3 rounded-md border border-border bg-card p-4">
          {selectedKey ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {selectedKey.namespace ? `${selectedKey.namespace}/` : ''}
                    {selectedKey.key}
                  </p>
                  {selectedKey.description && (
                    <p className="text-xs text-muted-foreground">{selectedKey.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium',
                      STATUS_TONE[rowStatus(selectedTr)],
                    )}
                  >
                    {STATUS_LABEL[rowStatus(selectedTr)]}
                  </span>
                  {selectedTr?.revisionCount && selectedTr.revisionCount > 0 ? (
                    <button
                      type="button"
                      onClick={() => setHistoryOpen(true)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-[11px] font-medium hover:bg-muted"
                    >
                      <History className="size-3" aria-hidden />
                      {selectedTr.revisionCount}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    Source · {project.data.sourceLanguageCode}
                  </Label>
                  <div className="min-h-[6rem] whitespace-pre-wrap rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                    {selectedKey.sourceText}
                  </div>
                  {selectedKey.context && (
                    <p className="text-[11px] text-muted-foreground">{selectedKey.context}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Target · {lang}</Label>
                  <Textarea
                    ref={editorRef}
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={onTextareaKeyDown}
                    disabled={!writable}
                    placeholder={writable ? 'Type translation…' : 'Read-only'}
                    rows={4}
                    className="min-h-[6rem]"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    {writable
                      ? 'Cmd/Ctrl-Enter to save + jump to next. Esc to discard. j/k or ↑/↓ in the key list to navigate.'
                      : 'Read-only — your role does not include write access for this language.'}
                  </p>
                </div>
              </div>

              <SuggestionRibbon
                k={selectedKey}
                lang={lang}
                glossary={glossary.data}
                aiSuggestion={
                  lastAISuggestion?.keyId === selectedKey.id ? lastAISuggestion.value : undefined
                }
                aiMeta={
                  lastAISuggestion?.keyId === selectedKey.id
                    ? {
                        provider: lastAISuggestion.provider,
                        model: lastAISuggestion.model,
                        latencyMs: lastAISuggestion.latencyMs,
                      }
                    : undefined
                }
                onApplySuggestion={writable ? (value) => setDraftValue(value) : undefined}
              />

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void onTranslateAgain()}
                  disabled={runTranslate.isPending}
                >
                  <Sparkles aria-hidden />
                  Translate again
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void onReject()}
                    disabled={!writable || upsert.isPending || !selectedTr?.value}
                  >
                    <X aria-hidden />
                    Reject
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void saveCurrent({ status: 'approved', advance: false })}
                    disabled={!writable || upsert.isPending || !draftValue}
                  >
                    <Check aria-hidden />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => void saveCurrent({ advance: true })}
                    disabled={!writable || upsert.isPending || !dirty}
                  >
                    {upsert.isPending ? 'Saving…' : 'Save + Next'}
                    <ChevronRight aria-hidden />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <EmptyState
              title="Pick a key to translate"
              description="Use the search and status filters on the left to narrow down."
            />
          )}
        </div>
      </div>

      <RevisionHistory
        open={historyOpen}
        keyID={selectedKey?.id}
        lang={lang}
        writable={writable}
        onClose={() => setHistoryOpen(false)}
        onRevert={(value) => {
          setDraftValue(value);
          setHistoryOpen(false);
        }}
      />
    </div>
  );
}

function BatchProgressBar({ progress }: { progress: TranslateProgress }) {
  const { status, total, translated, failed, remaining } = progress;
  const done = translated + failed;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const label = status === 'queued' ? 'Queued…' : 'Translating…';
  return (
    <div className="space-y-1.5 rounded-md border border-border bg-card px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <Loader2 className="size-3.5 animate-spin text-primary" aria-hidden />
          {label}
        </span>
        <span className="text-muted-foreground">
          {translated}/{total > 0 ? total : '—'} done
          {failed > 0 ? ` · ${failed} failed` : ''}
          {total > 0 ? ` · ${remaining} remaining` : ''}
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label="Translation progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${total > 0 ? pct : 8}%` }}
        />
      </div>
    </div>
  );
}

interface FilterChipsProps {
  counts: Record<StatusFilter, number>;
  active: Set<StatusFilter>;
  onToggle: (s: StatusFilter) => void;
}

function FilterChips({ counts, active, onToggle }: FilterChipsProps) {
  return (
    <div role="group" aria-label="Status filters" className="flex flex-wrap items-center gap-1.5">
      {ALL_STATUSES.map((s) => {
        const on = active.has(s);
        return (
          <button
            key={s}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(s)}
            className={cn(
              'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
              on
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-surface text-foreground hover:border-primary/40',
            )}
          >
            <span>{STATUS_LABEL[s]}</span>
            <Badge tone={on ? 'primary' : 'muted'}>{counts[s]}</Badge>
          </button>
        );
      })}
    </div>
  );
}

interface NamespaceChipsProps {
  namespaces: string[];
  active: Set<string>;
  onToggle: (ns: string) => void;
}

function NamespaceChips({ namespaces, active, onToggle }: NamespaceChipsProps) {
  return (
    <div role="group" aria-label="Namespace filters" className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Namespace</span>
      {namespaces.map((ns) => {
        const on = active.has(ns);
        return (
          <button
            key={ns || '_'}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(ns)}
            className={cn(
              'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-medium transition-colors',
              on
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-surface text-foreground hover:border-primary/40',
            )}
          >
            {ns || '(root)'}
          </button>
        );
      })}
    </div>
  );
}

interface RevisionHistoryProps {
  open: boolean;
  keyID: string | undefined;
  lang: string;
  writable: boolean;
  onClose: () => void;
  onRevert: (value: string) => void;
}

function RevisionHistory({ open, keyID, lang, writable, onClose, onRevert }: RevisionHistoryProps) {
  const enabled = open && Boolean(keyID) && Boolean(lang);
  const revisions = useTranslationRevisions(enabled ? keyID : undefined, enabled ? lang : undefined);
  return (
    <HistoryDrawer
      open={open}
      loading={revisions.isLoading}
      revisions={revisions.data ?? []}
      canRevert={writable}
      onRevert={onRevert}
      onClose={onClose}
    />
  );
}
